import requests from "../api/requests"
import { formatDate, pluralDays } from "../utils/dates"

const formatBRL = (amount) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(amount)

// "o Pix de R$ 97,00", or just "o Pix" when the amount is unknown.
const pixOf = (amount) => (typeof amount === "number" ? `o Pix de ${formatBRL(amount)}` : "o Pix")

// Routes that already show the pending payment front and center.
export const PENDING_UI_HIDDEN_PATHS = ["/plan/pix", "/subscription", "/awaiting-payment", "/unpaid"]

export const pendingActionLabel = (kind) => {
  if (kind === "pix") return "Pagar com Pix"
  if (kind === "card") return "Atualizar cartão"
  return "Ver fatura"
}

// A monthly-Pix charge is pending from D-3 (D47) while still on time: only an
// overdue one (past_due) carries grace days.
export const isUpcomingPix = (pending) =>
  pending?.kind === "pix" && pending.graceDaysRemaining === null && Boolean(pending.dueDate)

// Banner line (copy.md U1–U4, T3); the grace days follow it. trial is the
// status' promotional trial (selectTrial), when there is one.
export const pendingMessage = (pending, trial = null) => {
  if (trial?.state === "ended" && pending.kind === "pix") return "Seu teste terminou. Pague o Pix e seu acesso volta na hora."
  if (trial?.state === "running" && isUpcomingPix(pending)) {
    return `Seu teste termina em ${formatDate(pending.dueDate)}. Pague o Pix para continuar com acesso.`
  }
  if (isUpcomingPix(pending)) return `Seu Pix da renovação vence em ${formatDate(pending.dueDate)}.`
  if (pending.kind === "pix") return "Ainda não recebemos o seu Pix."
  if (pending.kind === "card") return "Não conseguimos cobrar seu cartão."
  return "Sua fatura ainda está em aberto."
}

export const graceMessage = (days) => `Seu acesso continua por mais ${pluralDays(days)}.`

// A Pix trial that ended unpaid (copy.md T13): /unpaid, Minha assinatura and
// the modal.
export const trialEndedNotice = (trial) => ({
  title: "Seu teste terminou",
  body: `Gostou do Cosmoclube? Pague ${pixOf(trial.amount)}${
    trial.payUntil ? ` até ${formatDate(trial.payUntil)}` : ""
  } e seu acesso volta na hora.`,
})

/**
 * Title and text of the pending payment on the modal and on Minha
 * assinatura (copy.md U5–U7, T3, T13, D63): what happened, how long access
 * goes on, the next step.
 */
export const pendingNotice = (pending, trial = null) => {
  const days = pending.graceDaysRemaining
  if (trial?.state === "ended" && pending.kind === "pix") return trialEndedNotice(trial)
  if (trial?.state === "running" && isUpcomingPix(pending)) {
    return {
      title: "Seu teste está terminando",
      body:
        `Pague ${pixOf(trial.amount)} até ${formatDate(pending.dueDate)} para continuar sem interrupção. ` +
        "Se não quiser continuar, cancele em Minha assinatura e nada será cobrado.",
    }
  }
  if (isUpcomingPix(pending)) {
    return {
      title: "Seu Pix da renovação está disponível",
      body: `Pague até ${formatDate(pending.dueDate)} para continuar com acesso sem interrupção.`,
    }
  }
  if (pending.kind === "pix") {
    return {
      title: "Ainda não recebemos o seu Pix",
      body:
        typeof days === "number" && days > 0
          ? `Tudo bem: você continua com acesso por mais ${pluralDays(days)}. Pague quando puder para não perder nada.`
          : "Pague quando puder para não perder nada.",
    }
  }
  if (pending.kind === "card") {
    return {
      title: "Não conseguimos cobrar seu cartão",
      body:
        "Isso acontece quando o cartão vence ou o limite acaba. Atualize o cartão e seu acesso continua normal" +
        (typeof days === "number" && days > 0 ? ` — você tem ${pluralDays(days)}.` : "."),
    }
  }
  return {
    title: "Sua fatura está em aberto",
    body: "Assim que ela for paga, seu acesso é liberado automaticamente.",
  }
}

// Boleto migration (copy.md U8, U13).
export const migrationNotice = (deadline) => ({
  title: "O boleto não é mais aceito",
  body: `Para continuar depois de ${formatDate(deadline)}, escolha uma nova forma de pagamento. Leva menos de um minuto.`,
})

/**
 * Settles a pending payment: Pix → the QR on /subscription; card → Stripe
 * portal to fix the card; invoice (boleto, first card payment with 3DS) →
 * the hosted invoice. The invoice URL is a bearer secret: only ever handed to
 * window.open, never logged.
 */
export const openPendingPayment = async (pending, navigate) => {
  if (pending.kind === "pix") {
    navigate("/subscription")
    return
  }

  if (pending.kind === "card") {
    try {
      const resp = await requests.createPortalSession({
        returnURL: window.location.origin,
      })
      if (resp?.data?.url) {
        window.location.href = resp.data.url
        return
      }
    } catch {
      // falls back to the hosted invoice below
    }
  }

  if (pending.invoiceUrl) {
    window.open(pending.invoiceUrl, "_blank", "noopener,noreferrer")
  }
}

// localStorage can throw (private mode, blocked storage): "seen" flags are a
// convenience, so failures just show the modal again.
export const wasSeen = (key) => {
  try {
    return Boolean(localStorage.getItem(key))
  } catch {
    return false
  }
}

export const markSeen = (key) => {
  try {
    localStorage.setItem(key, "1")
  } catch {
    // ignore
  }
}
